import React from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import { Grid, Hidden } from '@material-ui/core';
import Text from '../components/Text';
import Picture from '../components/Picture';
import Module from '../components/Module';
import Link from '../components/Link';
import PlayButton from '../components/PlayButton';

const useStyles = makeStyles(theme => ({
  section: {
    fontFamily: 'Spezia',
  },
  container: {},
  title: {
    paddingRight: theme.spacing(2),
    paddingLeft: theme.spacing(2),
  },
  cardContainer: {
    marginTop: 36,
    [theme.breakpoints.down('sm')]: {
      marginBottom: 32,
    },
  },
  cardWrapper: {},
  sectionTitle: {
    color: 'black',
    textAlign: 'center',
    marginBottom: 32,
    [theme.breakpoints.down('sm')]: {
      marginBottom: 24,
    },
  },
  cardLink: {
    textDecoration: 'none',
    display: 'block',
    height: '100%',
    color: 'transparent',
  },
  card: {
    width: '100%',
    position: 'relative',
    overflow: 'hidden',
    height: '100%',
    minHeight: 250,
    [theme.breakpoints.down('sm')]: {
      minHeight: 160,
    },
    '&:hover $cardImg img': {
      transform: 'scale(1.5)',
    },
  },
  cardContent: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    width: '100%',
    position: 'relative',
    zIndex: 2,
    padding: theme.spacing(2),
    textAlign: 'center',
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(1),
    },
  },
  cardMainTitle: {
    color: '#ffffff',
    fontWeight: 700,
    fontSize: '36px',
    [theme.breakpoints.down('sm')]: {
      fontSize: '30px',
    },
  },
  cardLogo: {
    maxWidth: '108px',
    height: '25px',
    marginTop: theme.spacing(1),
    '& img': {
      objectFit: 'contain',
      width: '100%',
      height: '100%',
      objectPosition: 'center',
    },
  },
  cardIcon: {
    position: 'absolute',
    transform: 'scale(0.5)',
    bottom: 0,
    right: 0,
  },
  cardImg: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    zIndex: 1,
    '&::after': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      zIndex: 1,
    },
    '& .Picture, & .Picture__img': {
      width: '100%',
      height: '100%',
    },
    '& img': {
      objectPosition: 'center',
      objectFit: 'cover',
      width: '100%',
      height: '100%',
      transform: 'scale(1)',
      transition: 'transform 0.5s ease-in-out',
    },
  },
}));

const CategoriesCard = ({item}) => {
  const classes = useStyles();
  const {title, logo_image, link, image, image_mobile} = item.field_value;

  return (
    <Link
      {...link?.field_value}
      className={classes.cardLink}
      aria-label={`CategoriesGridModule ${link?.field_value?.title}`}>
      <div className={classes.card}>
        <div className={classes.cardImg}>
          <Hidden smDown>
            <Picture {...image?.field_value} />
          </Hidden>
          {image_mobile?.field_value && (
            <Hidden mdUp>
              <Picture {...image_mobile.field_value} />
            </Hidden>
          )}
        </div>
        <div className={classes.cardContent}>
          <Text tag="span" className={classes.cardMainTitle}>
            {title.field_value}
          </Text>
          {logo_image?.field_value && (
            <div className={classes.cardLogo}>
              <Picture {...logo_image.field_value} />
            </div>
          )}
          <PlayButton className={classes.cardIcon}/>
        </div>
      </div>
    </Link>
  );
};

CategoriesCard.propTypes = {
  item: PropTypes.object.isRequired,
};

const CategoriesGridModule = ({title, item}) => {
  const classes = useStyles();
  const items = item.field_value || [];
  const itemCount = items.length;

  const renderGrid = () => {
    if (itemCount === 0) return null;

    return items.map((cardItem, index) => {
      let gridProps;

      if (itemCount > 3) {
        switch (index) {
          case 0:
            gridProps = {xs: 12, md: 4};
            break;
          case 1:
            gridProps = {xs: 6, md: 4};
            break;
          case 2:
            gridProps = {xs: 6, md: 4};
            break;
          default:
            gridProps = {xs: 12, md: 4};
            break;
        }
      } else {
        gridProps = {xs: 12, md: 4};
      }

      return (
        <Grid item {...gridProps} key={index} className={classes.cardWrapper}>
          <CategoriesCard item={cardItem}/>
        </Grid>
      );
    });
  };

  return (
    <Module className={classes.section} variant="full">
      <div className={classes.container}>
        <Text
          className={classes.title}
          variant="titleXXXL"
          component="h3"
          align="center"
          weight="bold"
          overline>
          {title.field_value}
        </Text>
        <Grid container justifyContent="center" spacing={1} className={classes.cardContainer}>
          {renderGrid()}
        </Grid>
      </div>
    </Module>
  );
};

CategoriesGridModule.defaultProps = {
  item: {
    field_type: 'repeater',
    field_value: [],
  },
  title: {
    field_type: 'text',
    field_value: '',
  },
};

CategoriesGridModule.propTypes = {
  title: PropTypes.object,
  item: PropTypes.object,
};

export default CategoriesGridModule;